import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react'

import {
    Controller,
    ControllerRenderProps,
    useFormContext,
    UseFormMethods
} from '../../../../../Vendor/react-hook-form/dist/index.ie11'
import { SelectRangeItem } from '..'
import { NONE_VALUE_STRING_DEFAULT } from '../../../Constants/DefaultValues'
import { CommonFormContext, FormContextType } from '../../../Contexts/CommonFormContext'
import { Rules } from '../../../Types/Types'
import PopperContent from '../../PopperContent'
import TextInputDropDown from '../../TextInputDropDown'
import { toItem } from '../ComponentFunctions/SelectFunctions'
import { SelectRangeItemState } from '../Constants'

/**
 * Component properties type.
 */
export type Props = {
    items: SelectRangeItem[]
    labelLeft: string
    value?: string
    onValueChange?: (str: string) => void
    name?: string
    rules?: Rules
    defaultValue?: string
    disabled?: boolean
    className?: string
    optionsClassName?: string
    variant?: 'outlined' | 'filled' | 'standard'
}

/**
 * Select box supporting Akixi proprietary range filter string value.
 *
 * @param items Select box items.
 * @param onValueChange Value change handler.
 * @param value The starting input of the select input.
 * @param labelLeft Label of the range list on left.
 * @param name Name of the select input - Used for hook forms.
 * @param rules Validation rules for the select input.
 * @param defaultValue Default value of the select input.
 * @param disabled Whether the select input is disabled.
 * @param className Class name for the select input.
 * @param optionsClassName Class name for the options in the dropdown.
 */
const SelectInput: React.FC<Props> = ({
    items,
    labelLeft,
    value,
    onValueChange,
    name: selectName,
    rules,
    defaultValue,
    disabled,
    className,
    optionsClassName,
    variant
}: Props): ReactElement => {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [textValue, setTextValue] = useState<string>(value ?? '')

    let methods: Definable<UseFormMethods<Record<string, unknown>>>

    if (selectName) {
        methods = useFormContext()
    }

    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const itemStateMapper = (it: SelectRangeItem, newValue: string): SelectRangeItem => {
        if (it.value === newValue) {
            // Update the selected item
            it.state = SelectRangeItemState.INCLUDED
        } else {
            // Update all of the items states to NONE.
            it.state = SelectRangeItemState.NONE
        }

        return it
    }

    const convertItems = (): SelectRangeItem[] =>
        items.map(
            (it: SelectRangeItem): SelectRangeItem =>
                itemStateMapper(it, value ?? (methods?.watch(selectName, '') as string))
        )

    const [itemsState, setItemsState] = useState<SelectRangeItem[]>(convertItems())

    useEffect(() => {
        setItemsState(convertItems())
    }, [items])

    // Update the items based on a string id value.
    const updateItems = (newValue: string) => {
        setItemsState(itemsState.map((it: SelectRangeItem): SelectRangeItem => itemStateMapper(it, newValue)))
        setTextValue(newValue)

        if (onValueChange) {
            onValueChange(newValue === NONE_VALUE_STRING_DEFAULT ? '' : newValue)
        }

        setIsOpen(false)
    }

    // What happens when the text field is changed.
    const handleTextFieldChange: (newValue: string) => void = (newValue: string): void => {
        updateItems(newValue)
    }

    // What happens when the user clicks on the text field.
    const handleClickOnTextField: () => void = (): void => {
        setIsOpen((wasOpen) => !wasOpen)
    }

    // What happens when you click on an item in the dropdown box.
    const handleItemClick: (clickedItem: SelectRangeItem) => void = (clickedItem: SelectRangeItem): void => {
        updateItems(clickedItem.value)

        if (selectName) {
            methods?.setValue(selectName, clickedItem.value)
        }
    }

    const textElemRef = useRef(null)

    let controlledSelectInput: Definable<ReactElement>

    if (methods) {
        const { control, watch } = methods
        const props = {
            control,
            name: selectName ?? '',
            rules,
            defaultValue: defaultValue ?? watch(selectName ?? '', ''),
            render: ({
                onChange,
                value: selectValue
            }: ControllerRenderProps<Record<string, unknown>>): ReactElement => (
                <React.Fragment>
                    <div className='select-range-wrapper'>
                        <TextInputDropDown
                            disabled={disabled}
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            value={selectValue}
                            labelLeft={labelLeft}
                            elementRef={textElemRef}
                            customDropdown={true}
                            isOpen={isOpen}
                            onClick={handleClickOnTextField}
                            onChange={(e: string): void => {
                                if (commonForm?.handleFormChange) {
                                    commonForm?.handleFormChange(selectName)
                                }

                                onChange(e)
                            }}
                            variant={variant}
                            classNames={className}
                        />
                    </div>
                    <PopperContent
                        anchorEl={textElemRef.current}
                        open={isOpen}
                        setOpen={setIsOpen}
                        isClickAwayHandled={true}
                        className={optionsClassName}
                    >
                        <div>
                            {isOpen && (
                                <div className='items-wrapper'>
                                    {itemsState?.map(
                                        (entry: SelectRangeItem): ReactElement => toItem(entry, handleItemClick)
                                    )}
                                </div>
                            )}
                        </div>
                    </PopperContent>
                </React.Fragment>
            )
        }

        controlledSelectInput = <Controller {...props} />
    }

    return selectName && controlledSelectInput ? (
        controlledSelectInput
    ) : (
        <React.Fragment>
            <div className='select-range-wrapper'>
                <TextInputDropDown
                    disabled={disabled}
                    value={textValue}
                    labelLeft={labelLeft}
                    elementRef={textElemRef}
                    customDropdown={true}
                    isOpen={isOpen}
                    onClick={handleClickOnTextField}
                    onChange={handleTextFieldChange}
                    variant={variant}
                    classNames={className}
                />
            </div>
            <PopperContent
                anchorEl={textElemRef.current}
                open={isOpen}
                setOpen={setIsOpen}
                isClickAwayHandled={true}
                className={optionsClassName}
            >
                <div>
                    {isOpen && (
                        <div className='items-wrapper'>
                            {itemsState?.map((entry) => toItem(entry, handleItemClick))}
                        </div>
                    )}
                </div>
            </PopperContent>
        </React.Fragment>
    )
}

export default SelectInput
